import { Button, Col, Form, Input, notification, Row } from "antd";
import SolutionTemplates from "../../components/SolutionTemplates";
import Heading from "../../components/Heading";
import PageContentWrapper from "../../components/PageContentWrapper";
import { Link, useLocation } from "react-router-dom";
import { ExecutingTemplate } from "../../types";
import { CrownOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import Search from "antd/lib/input/Search";
import { useEffect, useState } from "react";
import { TEMPLATES } from "../../enums/enums";
import { useAuth } from "../../contexts/Auth";
import { trackTemplateRequest, trackTemplateSearch } from "../../backend/tracking/templates";
import { getExecutingTemplates } from "../../utils/local-storage";
import { getEnvironmentService } from "../../backend";
import { GridListDataSource } from "../../components/GridList";
import MessageCard from "../../components/MessageCard";
import TemplateAvatar from "../../components/TemplateAvatar";

interface TemplatesProps {
    disableImport?: boolean;
}

const Templates = ({ disableImport }: TemplatesProps) => {
    const { isLoggedIn, profile } = useAuth();
    const currentProject = useRecoilValue(currentProjectState);
    const [searchQuery, setSearchQuery] = useState<string>();
    const [showRequestTemplate, setShowRequestTemplate] = useState(false);
    const [email, setEmail] = useState<string | undefined>(profile?.email);
    const [templates, setTemplates] = useState<GridListDataSource[]>([]);
    const [resources, setResources] = useState<GridListDataSource[]>([]);
    const [filteredTemplates, setFilteredTemplates] = useState<GridListDataSource[]>([]);
    const [filteredResources, setFilteredResources] = useState<GridListDataSource[]>([]);
    const [navigateState, setNavigateState] = useState<any>();
    const [defaultEnvId] = useState((useLocation().state as { envId: string } | undefined)?.envId);

    useEffect(() => {
        const loadResources = async () => {
            const [{ data: { components } }, { data: { stages } }] = await Promise.all([
                getEnvironmentService().getComponents(currentProject!.id),
                defaultEnvId ? Promise.resolve({ data: { stages: [] } }) : getEnvironmentService().getStages(currentProject!.id)
            ]);
            const environmentId = defaultEnvId || stages[0].id;

            // combine our Components with some of the Templates that we want to be displayed in the Resources section
            const combinedResources = [
                // add the templates that we want to display in the Resources section
                ...TEMPLATES.filter(item => item.group === "resources"),
                ...components
                    // Display only a few of our Components that dont require a VPC. These are prod values
                    .filter(item => ["Networking", "Amazon S3", "Amazon DynamoDB", "CloudFront + S3"].includes(item.name))
                    // map components to the format we need to display
                    .map(resource => {
                        return {
                            resource,           // we need this to pass along in the navigateState later
                            key: resource.id,
                            title: <>{resource.name} <CrownOutlined style={{ color: "goldenrod" }} /></>,
                            description: resource.description,
                            link: `/projects/${currentProject.id}/environments/${environmentId}/infrastructure/resources/${resource.id}/deploy`,
                            avatar: <TemplateAvatar name="aws" shape="square" size="large" />,
                            tags: [
                                {
                                    position: "top",
                                    value: <span style={{ color: "GrayText", fontSize: 12 }}>Starter</span>
                                }
                            ]
                        }
                    })
            ];
            setResources(combinedResources);
            setFilteredResources(combinedResources);
        }
        // add the templates that we want to display in the Templates list
        setTemplates(TEMPLATES.filter(item => item.group !== "resources"));
        setFilteredTemplates(TEMPLATES.filter(item => item.group !== "resources"));

        loadResources();
    }, [])

    useEffect(() => {
        setNavigateState(filteredResources
            .filter(item => !item.group)        // remove items that are added in this list from Templates, otherwise causes an issue
            .reduce((prev, curr) => {
                return {
                    state: {
                        ...prev.state,
                        // pass along the original resource object under its key
                        [curr.key]: (curr as any).resource
                    }
                };
            }, { state: {} }))
    }, [filteredResources])

    // e.g. you have three templates running on 1 environments. This array contains 1 object
    const [differentTemplateEnvironments, setDifferentTempalteEnvironments] = useState<{ envId: string, envName: string }[]>([]);

    const handleSearch = (searchQuery: string) => {
        // Filter templates
        const filteredTemplates = templates
            .filter(template => {
                if (searchQuery) {
                    return template.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (typeof template.title === "string" && template.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
                        (typeof template.description === "string" && template.description.toLowerCase().includes(searchQuery.toLowerCase()));
                } else {
                    return true;
                }
            });
        setFilteredTemplates(filteredTemplates);

        // Filter resources
        const filteredResources = resources
            .filter(resource => {
                if (searchQuery) {
                    return resource.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (typeof resource.title === "string" && resource.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
                        (typeof resource.description === "string" && resource.description.toLowerCase().includes(searchQuery.toLowerCase()));
                } else {
                    return true;
                }
            });
        setFilteredResources(filteredResources);

        setSearchQuery(searchQuery);
        setShowRequestTemplate(filteredTemplates.length === 0 && filteredResources.length === 0);

        if (searchQuery) {
            trackTemplateSearch(searchQuery, filteredTemplates.length);
        }
    }

    const handleRequestTemplate = () => {
        trackTemplateRequest(searchQuery!, email);

        notification.success({ message: "Thank you!", description: "Your submission has been received!" });
    }

    useEffect(() => {
        const getDifferentEnvironmentsFromExecutingTemplates = () => {
            const diffEnvironments = getExecutingTemplates().map((t: ExecutingTemplate) => {
                return {
                    envId: t.env.id,
                    envName: t.env.name || "Deployment"
                }
            })

            const uniqueEnvIds: string[] = [];

            setDifferentTempalteEnvironments(diffEnvironments.filter((e: { envId: string, envName: string }) => {
                const isDuplicate = uniqueEnvIds.includes(e.envId);
                if (!isDuplicate) {
                    uniqueEnvIds.push(e.envId);
                    return true;
                }
                return false;
            }));
        };

        getDifferentEnvironmentsFromExecutingTemplates();

        const interval = setInterval(async () => {
            getDifferentEnvironmentsFromExecutingTemplates()
        }, 6000);
        return () => clearInterval(interval);

    }, [])

    return (
        <Row gutter={24}>
            {!!differentTemplateEnvironments.length &&
                <Col xs={24}>
                    <PageContentWrapper size="medium">
                        {
                            differentTemplateEnvironments.length === 1 ?
                                <MessageCard
                                    type="info"
                                    text={
                                        <span>You have recently executed templates on <b>{differentTemplateEnvironments[0].envName}</b> environment. <Link to={`/projects/${currentProject.id}/environments/${differentTemplateEnvironments[0].envId}/active-templates`}>View details.</Link></span>
                                    }
                                />
                                :
                                <MessageCard
                                    type="info"
                                    text={
                                        <div>
                                            You have recently executed templates on {differentTemplateEnvironments.length} different environments:&nbsp;
                                            {
                                                differentTemplateEnvironments.map((o: { envId: string, envName: string }, index: number) => {
                                                    return (
                                                        <span>
                                                            <Link to={`/projects/${currentProject.id}/environments/${o.envId}/active-templates`}>{o.envName}</Link>{index === differentTemplateEnvironments.length - 1 ? `.` : `, `}
                                                        </span>
                                                    )
                                                })
                                            }
                                        </div>
                                    }
                                />
                        }
                    </PageContentWrapper>
                </Col>
            }

            {
                !disableImport &&
                <Col xs={24}>
                    <PageContentWrapper size="large">
                        <Heading
                            title="Deploy your apps directly from Git 🚀"
                            description="Import your existing app or create a new one with our ready-made templates."
                            align="center"
                            level={2}
                        />
                        {/* Filtering */}
                        <Row gutter={24}>
                            <Col md={10} offset={7}>
                                <Search
                                    size="large"
                                    placeholder="Search templates..."
                                    onKeyUp={(event: any) => {
                                        if (event.target.value === "") {
                                            setSearchQuery("");
                                            setShowRequestTemplate(false);
                                            // reset Templates and Resources to initial values (all items)
                                            setFilteredResources(resources);
                                            setFilteredTemplates(templates);
                                        }
                                    }}
                                    onSearch={handleSearch} />
                            </Col>
                        </Row>
                    </PageContentWrapper>
                </Col>
            }

            {
                showRequestTemplate &&
                <Col xs={24}>
                    <PageContentWrapper size="medium">
                        {
                            showRequestTemplate &&
                            <>
                                <Heading
                                    level={4}
                                    align="center"
                                    title="Didn't find what you were looking for?"
                                    description="Submit the cloud technology you prefer to see within our templates and consider it done. You can additionally leave your email and get notified when your request is up and running."
                                />
                                <Form>
                                    <Form.Item name="email">
                                        <Input.Group compact className="flex-justify-center">
                                            {
                                                !isLoggedIn &&
                                                <Input
                                                    size="large"
                                                    style={{ width: 'calc(100% - 500px)' }}
                                                    placeholder="Get notified via email (optional)"
                                                    value={email}
                                                    type="email"
                                                    onChange={(e) => setEmail(e.target.value)}
                                                />
                                            }
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                size="large"
                                                onClick={handleRequestTemplate}
                                            >
                                                Request '{searchQuery}' Template
                                            </Button>
                                        </Input.Group>
                                    </Form.Item>
                                </Form>
                            </>
                        }
                    </PageContentWrapper>
                </Col>
            }

            {/* Application Templates */}
            <Col xs={24}>
                <PageContentWrapper size="small">
                    <SolutionTemplates
                        title="Application Templates"
                        description="Deploy the most popular frameworks and techologies on a Kubernetes cluster of your choice."
                        dataSource={filteredTemplates}
                    />
                </PageContentWrapper>
            </Col>

            {/* Resources */}
            <Col xs={24}>
                <PageContentWrapper size="large">
                    <SolutionTemplates
                        title="AWS Resources"
                        description="Expand your apps with production-ready AWS infrastructure."
                        dataSource={filteredResources}
                        navigateState={navigateState}
                    />
                </PageContentWrapper>
            </Col>
        </Row >
    );
}

export default Templates;